// Blocked-shortcut matching. Pure — no DOM reads, no imports — so the whole
// table unit-tests without jsdom (same convention as `offscreenFraction` in
// ./index.ts).
//
// WHY THIS IS NOT `e.key` ALONE (the bug this module fixes)
// Matching on `e.key` looks obvious and is broken on two whole populations:
//
//   - macOS. Holding Option (altKey) makes the OS produce a dead key or an
//     accented glyph instead of the letter: Option+I -> "ˆ", Option+J -> "∆",
//     Option+C -> "ç", Option+U -> "¨" (some builds report `key === "Dead"`).
//     So the Cmd+Option+I/J/C DevTools rule and the Cmd+Option+U view-source
//     rule NEVER matched on real Mac hardware, in ANY browser, for as long as
//     they have shipped. Every macOS candidate had a free DevTools shortcut.
//   - Non-Latin layouts. On a Cyrillic layout Ctrl+C reports `key === "с"`
//     (Cyrillic es, U+0441), so clipboard blocking silently missed. Same for
//     Greek, Hebrew, Arabic.
//
// WHY NOT `e.code` ALONE
// `code` is the PHYSICAL key position, so it is immune to both problems above
// — but it breaks Dvorak, where the key that types "c" sits at physical
// `KeyI`. A code-only matcher would block the wrong key for those users.
//
// SO: match on `code` OR `key`. That is the only formulation covering all four
// populations at once — Latin QWERTY (both agree), macOS Option (`code`
// carries it), non-Latin layouts (`code` carries it), Dvorak (`key` carries
// it). `code` is consulted only when non-empty, because IME composition and
// virtual keyboards report `code === ""`.

export interface KeyDescriptor {
  /** `KeyboardEvent.key` — the character produced, layout- and modifier-dependent. */
  key: string;
  /** `KeyboardEvent.code` — the physical key. `""` for IME / virtual keyboards. */
  code: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

export interface ShortcutChord {
  name: string;
  /** When true, the chord is ignored inside the Monaco editor — neither
   *  recorded nor prevented — so the editor's own binding keeps working. */
  editorAllowed: boolean;
  /** Bare letters ("I") or key names ("F12"). Case-insensitive for letters. */
  keys: readonly string[];
  mods: (d: KeyDescriptor) => boolean;
}

/** True when the PHYSICAL key or the PRODUCED character is `id`.
 *
 * `id` is a bare letter ("I") or a key name ("F12"). PURE. */
export function matchesKeyIdentity(d: KeyDescriptor, id: string): boolean {
  // Produced character — case-insensitive so Shift+I ("I") and "i" both match.
  if (typeof d.key === "string" && d.key.toLowerCase() === id.toLowerCase()) {
    return true;
  }
  // Physical key. Empty on IME / virtual keyboards, where `key` is all we have.
  if (typeof d.code === "string" && d.code !== "") {
    // Letters live at `KeyX`; function keys use their own name verbatim.
    const physical = /^[A-Za-z]$/.test(id) ? `Key${id.toUpperCase()}` : id;
    if (d.code === physical) return true;
  }
  return false;
}

const DEVTOOLS_LETTERS = ["I", "J", "C"] as const;

export const BLOCKED_SHORTCUTS: readonly ShortcutChord[] = [
  // F12 opens DevTools on Windows/Linux.
  {
    name: "f12",
    editorAllowed: false,
    keys: ["F12"],
    mods: () => true,
  },
  // Windows/Linux DevTools shortcuts: Ctrl+Shift+I/J/C.
  {
    name: "ctrl+shift+devtools",
    editorAllowed: false,
    keys: DEVTOOLS_LETTERS,
    mods: (d) => (d.ctrlKey || d.metaKey) && d.shiftKey,
  },
  // Mac DevTools shortcuts: Cmd+Option+I/J/C (uses altKey, not shiftKey).
  // Also Safari's Console (Cmd+Option+C) and macOS Firefox's inspector.
  {
    name: "cmd+option+devtools",
    editorAllowed: false,
    keys: DEVTOOLS_LETTERS,
    mods: (d) => d.metaKey && d.altKey,
  },
  // View source. Ctrl+U on Windows/Linux, Cmd+Option+U on macOS/Safari — both
  // land here rather than in a devtools rule, because the loop returns on the
  // FIRST match and duplicating "U" would silently change the reported name.
  {
    name: "view-source",
    editorAllowed: false,
    keys: ["U"],
    mods: (d) => d.ctrlKey || d.metaKey,
  },
  // Save page (could be used to save the test offline).
  {
    name: "save-page",
    editorAllowed: false,
    keys: ["S"],
    mods: (d) => (d.ctrlKey || d.metaKey) && !d.shiftKey,
  },
  // Print (could print the test to PDF for offline reference).
  {
    name: "print",
    editorAllowed: false,
    keys: ["P"],
    mods: (d) => (d.ctrlKey || d.metaKey) && !d.shiftKey,
  },
  // Clipboard shortcuts — editor allows them so Monaco's native copy/paste
  // keeps working; outside the editor, blocked.
  {
    name: "ctrl+clipboard",
    editorAllowed: true,
    keys: ["C", "V", "X"],
    mods: (d) => (d.ctrlKey || d.metaKey) && !d.shiftKey,
  },
];

/** First chord in `BLOCKED_SHORTCUTS` matching `d`, or null. PURE.
 *
 * Order matters and mirrors the original table: the caller acts on the first
 * match, so `view-source` must precede any rule that also claims "U". */
export function matchBlockedShortcut(d: KeyDescriptor): ShortcutChord | null {
  for (const chord of BLOCKED_SHORTCUTS) {
    if (!chord.mods(d)) continue;
    for (const id of chord.keys) {
      if (matchesKeyIdentity(d, id)) return chord;
    }
  }
  return null;
}

/** Narrow a real `KeyboardEvent` to the pure descriptor. */
export function describeKeyEvent(e: KeyboardEvent): KeyDescriptor {
  return {
    key: typeof e.key === "string" ? e.key : "",
    code: typeof e.code === "string" ? e.code : "",
    ctrlKey: e.ctrlKey,
    metaKey: e.metaKey,
    shiftKey: e.shiftKey,
    altKey: e.altKey,
  };
}
